import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import THEME from '../../../constants/theme';

/**
 * ImageTextOverlay component renders a background image and overlays text.
 * Supports responsive height via sx prop!
 * @param {Object} props - Component props
 * @param {string} props.imageSrc - Image source URL
 * @param {string} props.text - Text to overlay
 * @param {string} props.height - Height value
 * @param {Object} props.boxSx - Box styling
 * @returns {React.Element} ImageTextOverlay component
 */
const ImageTextOverlay = ({
  imageSrc,
  text,
  height,
  boxSx,
  textSx,
  containerSx,
  overlayColor = 'rgba(0,0,0,0.1)' // NEW: customizable overlay color!
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // ... (rest unchanged)

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: height || {
          xs: '180px',
          sm: '220px',
          md: '260px',
          lg: '200px'
        },
        backgroundImage: `url(${imageSrc})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden',
        ...containerSx
      }}
    >
      {/* Overlay for darken effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          backgroundColor: overlayColor, // Use custom overlay color
          pointerEvents: 'none'
        }}
      />
      {/* Text Overlay */}
      <Box
        sx={{
          position: 'absolute',
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          top: 0,
          left: 0,
          zIndex: 2,
          ...boxSx
        }}
      >
        <Typography
          variant='body1'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            width: '100%',
            color: THEME.COLORS.NEUTRAL_WHITE,
            textShadow: '0 2px 10px rgba(0,0,0,0.25)',
            ...(isMobile
              ? {
                  textAlign: 'center',
                  px: 2,
                  fontSize: '1.1rem'
                }
              : {
                  textAlign: 'center',
                  px: 0,
                  fontSize: '1.3rem'
                }),
            ...textSx
          }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      </Box>
    </Box>
  );
};

export default ImageTextOverlay;
